import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { LoggerAbstractService, LogLevel } from 'bqx-logging';
import { first, Observable, ReplaySubject, retry } from 'rxjs';
import { AbstractAuthService } from './services/auth/auth.abstract.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private _canActicate = false;
  // the router asks for canActivate before authenticateUser() emits, so hand it an observable instead of a stale flag
  private _authResult$ = new ReplaySubject<boolean>(1);
  constructor(private authService: AbstractAuthService, private router: Router, private location: Location, private route: ActivatedRoute, private loggerService: LoggerAbstractService) {
    this.authService.authenticateUser()
      .pipe(retry(2)) // Retries the subscription two times in case of an error
      .subscribe({
        next: (val) => {
          if (val) {
            this.loggerService.postLogData(LogLevel.Info, 'User Authentication', JSON.stringify({ success: true, userId: this.authService.currentUserLanId, roles: this.authService.currentUserRoles }));
            if (window.location.pathname === '/') {
              this.routeToPage('/dashboard');
            }
          }
          this._canActicate = val;
          this._authResult$.next(val);
          this.authService.isBpLoggedIn$.next(val);
        },
        error: (error) => {
          // Handle the error if all retries fail
          this.loggerService.postLogData(LogLevel.Error, 'User Authentication', JSON.stringify({ success: false, error: error?.message ?? String(error) }));
          console.error('Authentication failed after retries:', error);
          this._canActicate = false;
          this._authResult$.next(false);
        }
      });
    this.applyRoutingRules();
  }

  public canActivate(): Observable<boolean> {
    return this._authResult$.pipe(first());
  }

  private applyRoutingRules(): void {
    this.router.events.subscribe((val: NavigationStart) => {
      if (this._canActicate) {
        if (val.url === '/' || (val.url === '/search_forms' && !this.authService.shouldHaveSearchAccess)) {
          this.routeToPage('/dashboard');
        }
      }
    });

    this.route.queryParams.subscribe((params) => {
      const { appid } = params;
      if (appid) {
        sessionStorage.setItem('app-uid-token', appid);
      }
    });
  }

  private routeToPage(name: string): void {
    this.location.replaceState(name);
    window.history.pushState(name, name);
    this.router.navigate([name]);
  }
}
